import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import withRouter from '../../../../routes/withRouter';
import DynamicRoutingDialog from './index';
import { autorun } from 'mobx';
import { isEqual } from 'lodash';
import { getCityRoutesForScenario } from '../../../../utilities/cityRoutesSessionStorage';
import { withFeatureFlags } from '@withFeatureFlags';
import appConstants from '@appConstants';

@inject("IBPlanningStore", "CommonStore")
@observer
class DynamicRoutingManager extends Component {
  constructor(props) {
    super(props);
    const initialCityRoutes = this.getCityRoutesForDynamicRouting();
    this.isDynamicRoutingTriggeredByShipmentSelection = initialCityRoutes?.length > 0;
    this.state = {
      cityRoutes: initialCityRoutes,
      isLoadingCityRoutes: true,
    };
  }

  componentDidMount() {
    if (!this.isDynamicRoutingTriggeredByShipmentSelection) {
      return;
    }
    // MobX autorun: reacts to IBPlanningStore (selectedRouteNameList, routesList) so city routes stay in sync and loader hides when routes load; see docs/features/SHIPMENT_SELECTION_TO_OPTIMIZATION.md
    this.autorunDisposer = autorun(() => {
        if (!this.props.show) return;
        const currentCityRoutes = this.getCityRoutesForDynamicRouting();
        if (currentCityRoutes.length === 0) {
          this.setState({ cityRoutes: [], isLoadingCityRoutes: false });
          return;
        }
        this.setState({ cityRoutes: currentCityRoutes });
        const selectedRoutesList = this.props.IBPlanningStore.selectedRouteNameList;
        const routesMatched = isEqual(selectedRoutesList, currentCityRoutes);
        const cityRoutesNotFound = !this.isCityRoutesPresentInRoutesList(currentCityRoutes);
        const routesListLoaded = this.props.IBPlanningStore.routesList?.length > 0;
        const routesReady = routesMatched || (cityRoutesNotFound && routesListLoaded);
        if (this.canShowDialogContent(routesReady)) {
          this.setState({ isLoadingCityRoutes: false });
        }
    });
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.isDynamicRoutingTriggeredByShipmentSelection && this.autorunDisposer) {
      this.autorunDisposer();
    }
  }

  handleBeforeUnload = () => {
    this.clearCityRoutesFromNavigationState(); // Clear city routes from navigation state to prevent re-triggering dynamic routing
  }

  /** True when routes are ready and parameters/waves APIs have responded (success, empty, or error). Used to show Dynamic Routing dialog content instead of loading state. */
  canShowDialogContent = (routesReady) => {
    const parametersLoaded = this.props.CommonStore?.parametersDataLoaded;
    const wavesLoaded = this.props.CommonStore?.wavesDataLoaded;
    const dependentDataLoaded = parametersLoaded && wavesLoaded;
    return routesReady && dependentDataLoaded;
  };

  isCityRoutesPresentInRoutesList = (cityRoutes = this.state.cityRoutes) => {
    const { routesList } = this.props.IBPlanningStore;
    if (!routesList || routesList.length === 0 || !cityRoutes?.length) {
      return !cityRoutes?.length;
    }

    const availableRoutes = routesList.map(route => route.routeName);
    const allRoutesExist = cityRoutes.every(
      routeName => availableRoutes.includes(routeName)
    );

    return allRoutesExist;
  };

  clearCityRoutesFromNavigationState = () => {
    const currentState = this.props.router?.location?.state || {};
    if (currentState?.cityRoutes?.length > 0) {
      const { cityRoutes, ...restState } = currentState;
      this.props.router.navigate(this.props.router.location.pathname, {
        replace: true,
        state: restState
      });
    }
  };

  handleClose = () => {
    this.props.onClose?.();
    if (this.isDynamicRoutingTriggeredByShipmentSelection) {
      this.clearCityRoutesFromNavigationState();
    }
  };

  getCityRoutesForDynamicRouting = () => {
    const isEnforcedDREnabled = this.props.featureFlags?.isFeatureEnabled?.(
      appConstants.features.EnableEnforcedDynamicRoutingFromShipmentSelection
    );
    if (!isEnforcedDREnabled) return [];
    const fromNavState = this.props.router?.location?.state?.cityRoutes;
    if (fromNavState?.length > 0) return fromNavState;
    const scenarioID = this.props.userData?.userProfile?.scenarioID;
    return scenarioID ? getCityRoutesForScenario(scenarioID) : [];
  };

  render() {
    if (!this.props.show) {
      return null;
    }

    return (
      <DynamicRoutingDialog
        {...this.props}
        onClose={this.handleClose}
        isCityRoutesPresentInRoutesList={this.isCityRoutesPresentInRoutesList}
        isDynamicRoutingTriggeredByShipmentSelection={this.isDynamicRoutingTriggeredByShipmentSelection}
        isDataLoading={this.isDynamicRoutingTriggeredByShipmentSelection && this.state.isLoadingCityRoutes}
        cityRoutes={this.state.cityRoutes}
        key={this.isDynamicRoutingTriggeredByShipmentSelection && this.state.isLoadingCityRoutes ? 'loading' : 'ready'}
      />
    );
  }
}

export default withFeatureFlags(withRouter(DynamicRoutingManager));
